import { getAnchorStore } from '../store/anchorStore';
import type {
    DrawerPaneAnchorRegistration,
    DrawerPaneAnchorSurface,
} from '../store/schema/DrawerPaneAnchorState';
import { getStore as getDrawerPaneStore } from '../store/store';
import getTabIdFromWindow from '../utils/getTabIdFromWindow';

function getAnchorMapKey(tabId: string, drawerPaneId: string): string {
    return `${tabId}:${drawerPaneId}`;
}

/**
 * The resolved add-in drawer mount: the element to portal into and the surface
 * that owns it. Returned from one place so no consumer has to re-derive
 * surface state from magic DOM ids.
 */
export interface ResolvedAddinDrawerMount {
    mountNode: HTMLElement;
    surface: DrawerPaneAnchorSurface;
}

/**
 * Returns the anchor registration (mount element + surface) for the given window,
 * or `null` when no external anchor has been set. Calendar event forms register
 * their content element here (which correctly handles multiple concurrent forms).
 */
export function getDrawerPaneAnchorRegistration(
    targetWindow: Window,
    drawerPaneId: string
): DrawerPaneAnchorRegistration | null {
    const tabId = getTabIdFromWindow(targetWindow);
    const registration = getAnchorStore().anchorMap.get(getAnchorMapKey(tabId, drawerPaneId));
    if (!registration) {
        return null;
    }

    // Ignore stale or cross-window anchor registrations so portal mounting
    // never targets a detached node or a different window.
    const ownerWindow = registration.element.ownerDocument?.defaultView;
    if (!registration.element.isConnected || ownerWindow !== targetWindow) {
        return null;
    }

    return registration;
}

/**
 * Resolves a specific visible drawer pane in this window to its registered
 * external anchor. Returns null when the pane is not shown or has no anchor.
 */
export function resolveVisibleDrawerMount(
    targetWindow: Window = window,
    drawerPaneId?: string
): ResolvedAddinDrawerMount | null {
    if (!drawerPaneId) {
        return null;
    }

    const tabId = getTabIdFromWindow(targetWindow);
    const tabMap = getDrawerPaneStore().paneStateMap.get(tabId);

    if (!tabMap) {
        return null;
    }

    const paneState = tabMap.get(drawerPaneId);
    if (!paneState?.isShown) {
        return null;
    }

    const registration = getDrawerPaneAnchorRegistration(targetWindow, drawerPaneId);
    if (registration) {
        return {
            mountNode: registration.element,
            surface: registration.surface,
        };
    }

    return null;
}
